import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElement } from '@stripe/stripe-js';
import axios from 'axios';
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import { ApiEndpoints } from '@utils/endpoints';

import { useAuth } from '@providers/AuthProvider';

interface IUpdatePaymentProps {}

const InfoPaneContainer = styled.div`
	padding: 20px;
	font-size: 2em;
`;

const SuccessMessage = styled.div`
	padding: 20px;
	font-size: 2em;
	color: green;
`;

const ErrorMessage = styled.div`
	padding: 20px;
	font-size: 2em;
	color: red;
`;

const UpdatePaymentContainer = styled.div`
	padding: 20px;
	font-family: 'Raleway';
	display: flex;
	justify-content: center;
	font-size: 1.2em;
	color: #0a2540;
	width: 480px;

	button {
		background-color: #635bff;
	}

	button {
		background: #635bff;
		border-radius: 3px;
		color: white;
		border: 0;
		padding: 12px 16px;
		margin-top: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.2s ease;
		display: block;
	}

	button:hover {
		filter: contrast(115%);
	}

	button:active {
		transform: translateY(0px) scale(0.98);
		filter: brightness(0.9);
	}

	button:disabled {
		opacity: 0.5;
		cursor: none;
	}

	input,
	select {
		display: block;
		font-size: 1.1em;
		width: 100%;
	}

	label {
		display: block;
	}

	a {
		color: #635bff;
		font-weight: 900;
	}

	small {
		font-size: 0.6em;
	}

	fieldset,
	input,
	select {
		border: 1px solid #efefef;
	}
`;

const PaymentForm = styled.form`
	border: #f6f9fc solid 1px;
	border-radius: 3px;
	padding: 20px;
	margin: 20px 0;
	width: 480px;
	box-shadow: 0 30px 50px -20px rgb(50 50 93 / 25%), 0 30px 60px -30px rgb(0 0 0 / 30%);
`;

interface IPaymentInfo {
	brand: string;
	last4: string;
	expMonth: string;
	expYear: string;
}

interface IPaymentInfoPaneProps {
	paymentInfo: IPaymentInfo;
}

const PaymentInfoPane: FC<IPaymentInfoPaneProps> = ({
	paymentInfo: { brand, expMonth, expYear, last4 },
}) => {
	return (
		<InfoPaneContainer>
			<div>Payment Info: Card Type: {brand}</div>
			<div>Last4: {last4}</div>
			<div>
				Expires: {expMonth} / {expYear}
			</div>
		</InfoPaneContainer>
	);
};

const UpdatePayment: FC<IUpdatePaymentProps> = ({}) => {
	const stripe = useStripe();
	const elements = useElements();
	const [errorMessage, updateErrorMessage] = useState<string | undefined>();
	const [successMessage, updateSuccessMessage] = useState<string | undefined>();
	const [currentPaymentInfo, updateCurrentPaymentInfo] = useState<IPaymentInfo | undefined>();

	const { getAuthToken, email: memberEmail } = useAuth();

	useEffect(() => {
		getAuthToken().then((authorizationToken) =>
			axios
				.get<IPaymentInfo>(`${ApiEndpoints.stripe}/payment-method`, {
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
						authorizationToken,
					},
				})
				.then((pm) => updateCurrentPaymentInfo(pm.data))
		);
	});

	const handleSubmit = async (e) => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		e.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		const paymentMethod = (
			await stripe.createPaymentMethod({
				type: 'card',
				card: elements.getElement(CardElement) as StripeCardElement,
				billing_details: {
					email: memberEmail,
				},
			})
		)?.paymentMethod;

		const authorizationToken = await getAuthToken();
		const { error: backendError } = await fetch(`${ApiEndpoints.stripe}/payment-method`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
				authorizationToken,
			},
			body: JSON.stringify({
				paymentMethod,
			}),
		}).then((r) => r.json());

		if (backendError) {
			updateErrorMessage(backendError.message);
			return;
		}

		updateSuccessMessage(`Updated Payment Info`);
	};

	return (
		<>
			{currentPaymentInfo && <PaymentInfoPane paymentInfo={currentPaymentInfo} />}
			<UpdatePaymentContainer>
				<PaymentForm id="payment-form" onSubmit={handleSubmit}>
					<label htmlFor="card">Card</label>
					<CardElement id="card" />
					<button type="submit">Update Payment Method</button>
				</PaymentForm>
			</UpdatePaymentContainer>
			<SuccessMessage>{successMessage}</SuccessMessage>
			<ErrorMessage>{errorMessage}</ErrorMessage>
		</>
	);
};

export default UpdatePayment;
